import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button, EmptyState, Input, PageLoading } from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { listAssignedPatients } from "../../lib/api-client";

export function PharmacistPatientsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const pharmacistId = user!.pharmacistId!;
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const { data: patients, isLoading } = useQuery({
    queryKey: ["assigned-patients", pharmacistId],
    queryFn: () => listAssignedPatients(pharmacistId),
  });

  if (isLoading) return <PageLoading />;

  const filtered = (patients ?? []).filter((p) =>
    `${p.firstName} ${p.lastName} ${p.patientCode}`.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <h1 className="text-[2.25rem] font-semibold leading-none text-charcoal-900">{t("pharmacist.patients.title")}</h1>

      <div className="flex items-center justify-between gap-5">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal-400" />
          <Input placeholder={t("pharmacist.patients.search")} value={query} onChange={(e) => setQuery(e.target.value)} className="ps-9" />
        </div>
        <Button size="sm">
          <Plus className="h-4 w-4" /> {t("pharmacist.patients.addPatient")}
        </Button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={t("pharmacist.patients.empty")} />
      ) : (
        <div className="overflow-x-auto">
          <div className="grid min-w-[600px] grid-cols-[2fr_1fr_1fr] gap-5 border-b border-charcoal-900/25 pb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-charcoal-500">
            <div>{t("pharmacist.patients.name")}</div>
            <div>{t("pharmacist.patients.mrn")}</div>
            <div>{t("pharmacist.patients.city")}</div>
          </div>
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/pharmacist/patients/${p.id}`)}
              className="grid min-w-[600px] w-full grid-cols-[2fr_1fr_1fr] items-center gap-5 border-b border-charcoal-900/[0.12] py-[15px] text-start transition-colors hover:bg-primary-600/5"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="h-4 w-[3px] shrink-0 bg-transparent" />
                <span className="text-[17px] font-semibold text-charcoal-900">
                  {p.firstName} {p.lastName}
                </span>
              </div>
              <div className="text-sm tabular-nums text-charcoal-700">{p.patientCode}</div>
              <div className="text-sm text-charcoal-700">{p.city ?? "—"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
